import scssVariables from "@/assets/styles/variables.scss"
import EventBus, { EVENTS } from "@/lib/event-bus"
import { isPresent } from "@/lib/utils"

import { BLOCK_TYPES } from "@shared/blocks"
import { TYPES, BLOCK_PAGE_ATTRIBUTES } from "@shared/page-attributes"

export const BLOCK_DEFAULT_HEIGHT_RATIO = 100

export const ROLES = {
  PAGE: "page",
  BLOCK: "block",
  BLOCK_CONTAINER: "block-container"
}

export const BLOCK_COLORS = {
  WHITE: "white",
  LIGHT_GREY: "light_grey",
  GREY: "grey",
  BLACK: "black",
  PRIMARY: "primary"
}

export const COLOR_MODES = {
  LIGHT: "light",
  DARK: "dark"
}

export const BACKGROUND_COLORS = {
  [BLOCK_COLORS.WHITE]: scssVariables["color-ui--white"],
  [BLOCK_COLORS.LIGHT_GREY]: scssVariables["color-ui--grey-20"],
  [BLOCK_COLORS.GREY]: scssVariables["color-ui--grey-60"],
  [BLOCK_COLORS.BLACK]: scssVariables["color-ui--black"]
}

export const TEXT_COLORS = {
  [BLOCK_COLORS.WHITE]: scssVariables["color-ui--white"],
  [BLOCK_COLORS.LIGHT_GREY]: scssVariables["color-ui--grey-20"],
  [BLOCK_COLORS.GREY]: scssVariables["color-ui--grey-70"],
  [BLOCK_COLORS.BLACK]: scssVariables["color-ui--grey-90"]
}

export const BLOCK_ACTIONS = {
  OPEN_URL: "open_url",
  OPEN_PAGE: "open_page",
  OPEN_EMAIL: "open_email",
  TRIGGER_PURCHASE: "trigger_purchase",
  TRIGGER_FORM: "trigger_form",
  TRIGGER_OVERLAY: "trigger_overlay",
  SHARE: "share"
}

export const LIVESTREAM_PROVIDERS = {
  YOUTUBE: "youtube",
  ZOOM: "zoom",
  VIMEO: "vimeo",
  FACEBOOK: "facebook"
}

export const DYNAMIC_ORDER_BY = ["created_desc", "created_asc", "alphabetical"]

// Note: Must Match backend/app/javascript/components/page-builder/blocks/HeroBlock.vue
export const HERO_HEIGHT_RATIO_RANGE = {
  MIN: 10,
  MAX: 300
}

export const isBlockActionLink = action =>
  [BLOCK_ACTIONS.OPEN_URL, BLOCK_ACTIONS.OPEN_PAGE].includes(action)

export const withRatio = (baseValue, ratio, opts = {}) => {
  const options = { unit: "px", integer: false, ...(opts || {}) }
  const value =
    Math.round((0.01 * baseValue * ratio + Number.EPSILON) * 100) / 100

  return `${options.integer ? Math.round(value) : value}${
    options.unit ? options.unit : ""
  }`
}

export const ratioToMultiplier = (ratio, min, max) => {
  if (ratio < min || ratio > max) {
    throw "Ratio Out Of Range"
  }

  return Math.round(((ratio - min) / (max - min)) * 100) / 100
}

export const toPadding = (top, x, bottom, opts = {}) => {
  const options = { unit: "rem", baseValue: 1, ...(opts || {}) }

  const paddingTop = withRatio(options.baseValue, top, { unit: options.unit })
  const paddingX = withRatio(options.baseValue, x, { unit: options.unit })
  const paddingBottom = withRatio(options.baseValue, bottom, {
    unit: options.unit
  })

  return `${paddingTop} ${paddingX} ${paddingBottom}`
}

export const triggerBlockAction = (
  blockAction,
  hasPurchased = false,
  disableClose = false
) => {
  const {
    action,
    overlay_id,
    form_id,
    products,
    secondary_action
  } = blockAction
  const { TRIGGER_FORM, TRIGGER_OVERLAY, TRIGGER_PURCHASE } = BLOCK_ACTIONS

  switch (action) {
    case TRIGGER_FORM:
      form_id &&
        EventBus.$emit(EVENTS.TRIGGER_FORM, {
          formId: form_id,
          continueAction: secondary_action,
          disableClose
        })
      break
    case TRIGGER_OVERLAY:
      overlay_id && EventBus.$emit(EVENTS.TRIGGER_OVERLAY, overlay_id)
      break
    case TRIGGER_PURCHASE:
      if (hasPurchased) {
        secondary_action &&
          EventBus.$emit(EVENTS.TRIGGER_BLOCK_ACTION, secondary_action)
      } else {
        const productIds = products.map(product => product.id)

        productIds &&
          EventBus.$emit(EVENTS.TRIGGER_CHECKOUT, {
            productIds: productIds
          })
      }
      break
  }
}

export const buildBlockMountedEvent = (getBlock, element) => ({
  getBlock,
  element
})

export const getBlockImage = (block, pageAttributeValues = {}) => {
  const attribute = block.data[BLOCK_PAGE_ATTRIBUTES.IMAGE_PAGE_ATTRIBUTE]

  return isPresent(attribute) && attribute.type === TYPES.IMAGE
    ? pageAttributeValues[attribute.slug]
    : isPresent(block.data.image)
    ? block.data.image
    : null
}

export const getBlockVideo = (block, pageAttributeValues = {}) => {
  const attribute = block.data[BLOCK_PAGE_ATTRIBUTES.VIDEO_PAGE_ATTRIBUTE]

  return isPresent(attribute) && attribute.type === TYPES.VIDEO
    ? pageAttributeValues[attribute.slug]
    : isPresent(block.data.video)
    ? block.data.video
    : null
}

export const getBlockAudio = (block, pageAttributeValues = {}) => {
  const attribute = block.data[BLOCK_PAGE_ATTRIBUTES.AUDIO_PAGE_ATTRIBUTE]

  return isPresent(attribute) && attribute.type === TYPES.AUDIO
    ? pageAttributeValues[attribute.slug]
    : isPresent(block.data.audio)
    ? block.data.audio
    : null
}

export const getBlockItems = (block, pageAttributeValues = {}) => {
  const attribute = block.data[BLOCK_PAGE_ATTRIBUTES.PAGE_ATTRIBUTE]

  return isPresent(attribute) && attribute.type === TYPES.COLLECTION
    ? pageAttributeValues[attribute.slug]?.items || []
    : isPresent(block.data.collection)
    ? block.data.collection?.items || []
    : []
}

// Calcuate the relative height of the block to our "base" height (representative of a generic smartphone)
export const scaledHeight = height =>
  `calc(${height} * var(--app-height-ratio-initial))`

export { BLOCK_TYPES }
